import { useCallback } from 'react';
import { setAgentState as setAgentStateSdk } from '../../services/cxone-sdk.service';
import { logger } from '../../utils/logger';
import { AgentState, CallState, UnavailableCode } from '../../types';
import { releasePostCallUiLocks } from '../agent-widget/post-call-recovery.util';
import { initialCallState } from './types';

interface UseAgentStateActionOptions {
  unavailableCodes: UnavailableCode[];
  handleSessionError: (error: Error) => void;
  setCallState: React.Dispatch<React.SetStateAction<CallState>>;
  setAgentStateInternal: React.Dispatch<React.SetStateAction<AgentState>>;
  setIsInAcw: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRinging: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAgentLegActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useAgentStateAction({
  unavailableCodes,
  handleSessionError,
  setCallState,
  setAgentStateInternal,
  setIsInAcw,
  setIsRinging,
  setIsAgentLegActive,
}: UseAgentStateActionOptions) {
  return useCallback(
    async (state: string, reasonCode?: string) => {
      const matchingCode = reasonCode
        ? unavailableCodes.find((code) => code.outStateId === reasonCode)
        : null;
      const reasonName = matchingCode?.outStateName || null;
      const nextReason = state?.toLowerCase() === 'unavailable' ? reasonName : null;
      const nextReasonCode = state?.toLowerCase() === 'unavailable' ? reasonCode || null : null;
      const goingAvailable = state?.toLowerCase() === 'available';

      try {
        await setAgentStateSdk(state, reasonName || undefined);
      } catch (error) {
        logger.error('[Events] Failed to set agent state:', error);
        if (error instanceof Error) {
          handleSessionError(error);
        }
        throw error;
      }

      setAgentStateInternal({
        state,
        reason: nextReason,
        reasonCode: nextReasonCode,
        stateStartTime: new Date(),
      });

      if (goingAvailable) {
        setIsInAcw(false);
        setCallState((prev) => (prev.status === 'idle' ? prev : initialCallState));
        setIsRinging(false);
        setIsAgentLegActive(false);
        releasePostCallUiLocks();
      }
    },
    [
      unavailableCodes,
      handleSessionError,
      setCallState,
      setAgentStateInternal,
      setIsInAcw,
      setIsRinging,
      setIsAgentLegActive,
    ]
  );
}
